import { motion } from 'framer-motion';
import { ArrowRightLeft, MapPin } from 'lucide-react';
import { useI18n } from '../lib/i18n';

type Props = {
  me: { displayName: string; avatarUrl?: string | null };
  them: { displayName: string; avatarUrl?: string | null; city?: string | null };
  score: number;              // 0..1
  iCanGive: number[];
  iCanReceive: number[];
  onPropose?: () => void;
};

export function MatchVsCard({ me, them, score, iCanGive, iCanReceive, onPropose }: Props) {
  const { t } = useI18n();
  const pct = Math.round(score * 100);
  const tier =
    pct >= 80 ? { label: t('matches.perfectMatch'), color: 'text-lime', shadow: 'shadow-glow-lime' } :
    pct >= 60 ? { label: t('matches.strongMatch'),  color: 'text-cyan', shadow: 'shadow-glow-cyan' } :
                { label: t('matches.possibleMatch'),color: 'text-haze', shadow: '' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-3xl glass-strong p-5 ${tier.shadow}`}
    >
      {/* arco de score */}
      <div className="absolute -top-12 right-[-30px] h-40 w-40 rounded-full bg-holo opacity-15 blur-2xl" />

      <div className="flex items-center justify-between">
        <span className={`text-xs uppercase tracking-[0.25em] font-bold ${tier.color}`}>
          {tier.label}
        </span>
        <span className={`font-display font-extrabold text-3xl ${tier.color} tabular-nums`}>
          {pct}%
        </span>
      </div>

      {/* VS arena */}
      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <Avatar name={me.displayName} avatar={me.avatarUrl} ring="lime" align="end" />
        <div className="grid h-12 w-12 place-items-center rounded-full border border-line bg-void">
          <ArrowRightLeft size={18} className="text-flame" />
        </div>
        <Avatar name={them.displayName} avatar={them.avatarUrl} ring="flame" align="start" sub={them.city ?? undefined} />
      </div>

      {/* listas */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <ItemList title={t('matches.iGive')} numbers={iCanGive} accent="text-lime" />
        <ItemList title={t('matches.iGet')} numbers={iCanReceive} accent="text-flame" />
      </div>

      <button
        onClick={onPropose}
        className="btn-primary mt-5 w-full"
      >
        {t('matches.proposeTrade')}
      </button>
    </motion.div>
  );
}

function Avatar({
  name, avatar, ring, align, sub,
}: {
  name: string; avatar?: string | null;
  ring: 'lime' | 'flame'; align: 'start' | 'end'; sub?: string;
}) {
  const initials = name.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const ringClass = ring === 'lime' ? 'ring-lime/70 shadow-glow-lime' : 'ring-flame/70 shadow-glow-flame';
  return (
    <div className={`flex items-center gap-2 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
      {align === 'end' && <Text name={name} sub={sub} align="right" />}
      <div className={`grid h-12 w-12 place-items-center rounded-full bg-graphite ring-2 ${ringClass} font-display font-bold`}>
        {avatar ? (
          <img src={avatar} alt={name} className="h-full w-full rounded-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      {align === 'start' && <Text name={name} sub={sub} align="left" />}
    </div>
  );
}

function Text({ name, sub, align }: { name: string; sub?: string; align: 'left' | 'right' }) {
  return (
    <div className={align === 'right' ? 'text-right' : 'text-left'}>
      <div className="font-semibold text-sm text-foam leading-tight line-clamp-1">{name}</div>
      {sub && (
        <div className="mt-0.5 flex items-center gap-1 text-2xs text-haze">
          {align === 'left' && <MapPin size={10} />}
          <span>{sub}</span>
          {align === 'right' && <MapPin size={10} />}
        </div>
      )}
    </div>
  );
}

function ItemList({ title, numbers, accent }: { title: string; numbers: number[]; accent: string }) {
  const preview = numbers.slice(0, 4);
  const rest = numbers.length - preview.length;
  return (
    <div className="rounded-2xl border border-line/70 bg-ink/40 p-3">
      <div className={`text-2xs uppercase tracking-widest font-bold ${accent}`}>{title}</div>
      <div className="mt-1.5 flex flex-wrap gap-1 font-mono text-sm tabular-nums">
        {preview.map((n) => (
          <span key={n} className="rounded-md bg-graphite px-1.5 py-0.5">{n}</span>
        ))}
        {rest > 0 && <span className="text-haze text-xs self-end">+{rest}</span>}
      </div>
    </div>
  );
}
